import { Component, ElementRef, Input, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { Subject } from 'rxjs';
import { debounceTime, distinctUntilChanged } from 'rxjs/operators';
import { AbstractControl } from '@angular/forms';
import { digitsRegex } from '../plus-minus-input/plus-minus-input.component';
import tippy, { Instance, Props, SingleTarget } from 'tippy.js';
import { InstrumentDetailFullOut } from 'src/app/services/rest-api/rest-api';

@Component({
  selector: 'libero-strategy-instrument-dropdown-input',
  templateUrl: './strategy-instrument-dropdown-input.component.html',
  styleUrls: ['./strategy-instrument-dropdown-input.component.scss'],
  standalone: false
})
export class StrategyInstrumentDropdownInputComponent implements OnInit, OnDestroy {
  @Input() public instrument?: InstrumentDetailFullOut;
  @Input() public minDisabledTooltip?: string;
  @Input() public maxDisabledTooltip?: string;
  @Input() public disabledTextInput?: boolean;

  @ViewChild('tooltipUnderMinimumContent') public tooltipUnderMinimumContent?: ElementRef;
  private tooltipUnderMinHoverable?: ElementRef;
  @ViewChild('tooltipUnderMinHoverable') public set minContent(content: ElementRef) {
    if(content) {
        this.tooltipUnderMinHoverable = content;
        this.initMinDisabledTooltip();
     }
  }
  private minTooltipInstance: Instance<Props> | null = null;

  @ViewChild('tooltipOverMaximumContent') public tooltipOverMaximumContent?: ElementRef;
  private tooltipOverMaxHoverable?: ElementRef;
  @ViewChild('tooltipOverMaxHoverable') public set maxContent(content: ElementRef) {
    if(content) {
        this.tooltipOverMaxHoverable = content;
        this.initMaxDisabledTooltip();
     }
  }
  private maxTooltipInstance: Instance<Props> | null = null;

  @Input() public formName!: AbstractControl;
  @Input() public min = 0;
  @Input() public max = 100;

  @Input() public cash = false;

  public currentValueDebouncer = new Subject<number>();
  public toggled = false;

  public currentInputValue = 0;

  public ngOnInit(): void {
    this.currentValueDebouncer.pipe(
      debounceTime(100),
      distinctUntilChanged())
      .subscribe(value => {
        this.formName.patchValue({value});
        this.formName.markAsTouched();
        this.formName.markAsDirty();
      }
    );

    this.initValue();
  }

  private initValue() {
    if (this.formName?.value) {
      this.updateValue(this.formName.value.value);
    }
  }

  public toggle(): void {
    this.toggled = !this.toggled;
  }

  public invalidInput(): boolean {
    if (this.currentInputValue < this.min) {
      return true;
    }

    if (this.currentInputValue > this.max) {
      return true;
    }

    return false;
  }

  public clickedMinus(): void {
    if (this.currentInputValue - 1 >= this.min) {
      this.updateValue(this.currentInputValue - 1);
    }
  }

  public clickedPlus(): void {
    if (this.currentInputValue + 1 <= this.max) {
      this.updateValue(this.currentInputValue + 1);
    }
  }

  private updateValue(newValue: number | string) {
    if(
      !digitsRegex.test(String(newValue)) &&
      (!(newValue + '').includes('.') ||
      !(newValue + '').includes(','))
    ) {
      const valueMaxConsidered: number = +newValue >= this.max ? this.max : +newValue;
      setTimeout(() => {
        this.currentInputValue = valueMaxConsidered;
        this.currentValueDebouncer.next(valueMaxConsidered);
      }, 0);

    } else {
      // overwrite wrong input with prev value
      setTimeout(() => {
        this.currentInputValue = parseInt(this.formName.value.value);
      }, 100);
    }
  }

  public inputUpdated(): void {
    this.updateValue(this.currentInputValue);
  }

  private initMinDisabledTooltip(): void {
    if(this.minTooltipInstance) {
      this.minTooltipInstance.destroy();
      this.minTooltipInstance = null;
    }
    if(this.tooltipUnderMinHoverable && this.tooltipUnderMinimumContent) {
      this.minTooltipInstance = tippy(this.tooltipUnderMinHoverable.nativeElement, {
        content: this.tooltipUnderMinimumContent.nativeElement.innerHTML,
        allowHTML: true,
        placement: 'auto',
      }) as unknown as Instance<Props>;
    }
  }

  private initMaxDisabledTooltip(): void {
    if(this.maxTooltipInstance) {
      this.maxTooltipInstance.destroy();
      this.maxTooltipInstance = null;
    }
    if(this.tooltipOverMaxHoverable && this.tooltipOverMaximumContent) {
      this.maxTooltipInstance = tippy(this.tooltipOverMaxHoverable.nativeElement as SingleTarget, {
        content: this.tooltipOverMaximumContent.nativeElement.innerHTML,
        allowHTML: true,
        placement: 'auto',
      }) as Instance<Props>;
    }
  }

  public ngOnDestroy(): void {
    this.currentValueDebouncer.unsubscribe();
  }
}
